/* eslint-disable @typescript-eslint/no-empty-function */
import { type PropsWithChildren, createContext, useRef } from 'react'

import { type SortByKeys, SORT_BY_OPTIONS } from '@/constants/search'
import { updateDateRangeFilters } from '@/domain/filters'
import { type DateFilterInput, type DateRangeFilterInput, AdditionalFlags } from '@/generated'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { useSearchActions } from '@/hooks/srp/useSearchActions'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { sendToLogger } from '@/utils/sendToLogger'

import { useSrpContext } from './SrpContext'

export type SearchContextClient = {
  handleDateFiltersChange: (canonicalName: string, values: string) => void
  resetDateFilter: (filterName: DateFilterInput['filterName'], preventRefetch?: boolean) => void
  handleDateRangeFiltersChange: (
    filterName: DateRangeFilterInput['filterName'],
    currentDateRangeFilters: DateRangeFilterInput[],
    start?: DateRangeFilterInput['start'],
    end?: DateRangeFilterInput['end']
  ) => void
  handleNewSearch: (selectedCategoryId: number, keyword: string) => Promise<void>
  handleSortByChange: (sortKey: SortByKeys) => Promise<void>
  hasRefetchUrlError?: boolean
  loadingFilters?: boolean
  loadingResults: boolean
  resetAllFilters: () => void
  seeAllTopAds: () => Promise<void>
}

export const SearchContext = createContext<SearchContextClient>({
  handleDateFiltersChange: () => {},
  resetDateFilter: () => {},
  handleDateRangeFiltersChange: () => {},
  handleNewSearch: async () => {},
  handleSortByChange: async () => {},
  loadingResults: false,
  resetAllFilters: () => {},
  seeAllTopAds: async () => {},
})

export const SearchProvider = ({ children }: PropsWithChildren) => {
  /**
   * Make sure the new variable is getting set with the context variable so we can migrate over slowly without breaking
   * the rest of the application
   */
  const { data, loadingFilters, loadingResults, setLoadingStates } = useGetSearchResultsData()
  const { refetchResults: refetchListings } = useSearchActions()

  const { categoryPath } = useSrpContext()

  /** Tracks whether the user has interacted with the sort dropdown */

  const hasUserSelectedSort = useRef<boolean>(false)

  /** Every SRP error should log the error and track with analytics */
  const catchError = (err: unknown) => {
    sendToLogger(err)
  }

  /** Sorting */
  const handleSortByChange = async (newSortKey: SortByKeys) => {
    hasUserSelectedSort.current = true
    try {
      // If a new sort key was passed (in handleSortByChange), then set the sortByOption
      const sortByOption = !!newSortKey && SORT_BY_OPTIONS[newSortKey]
      await refetchListings(data, {
        newSortOption: { by: sortByOption.sort, direction: sortByOption.order },
      })
    } catch (err) {
      catchError(err)
    }
  }

  const handleDateFiltersChange = async (canonicalName: string, value: string) => {
    const trackingLabel = `${canonicalName} = [${value}]`
    await refetchListings(
      data,
      { newDateFilters: [{ filterName: canonicalName, value }] },
      trackingLabel
    )
  }

  const resetDateFilter = async (
    filterName: DateFilterInput['filterName'],
    preventRefetching?: boolean
  ) => {
    if (preventRefetching) return
    const trackingLabel = filterName
    await refetchListings(data, { newDateFilters: [] }, trackingLabel)
  }

  /** Change date range filters  */
  const handleDateRangeFiltersChange = async (
    filterName: DateRangeFilterInput['filterName'],
    currentDateRangeFilters: DateRangeFilterInput[],
    start?: DateRangeFilterInput['start'],
    end?: DateRangeFilterInput['end']
  ) => {
    const { isNewValue, updatedDateRange } = updateDateRangeFilters({
      dateRangeFilters: currentDateRangeFilters,
      end,
      filterName,
      start,
    })

    /** It should not trigger an update for the listings if the value hasn't changed */
    if (!isNewValue) return

    const trackingLabel = `${filterName} = [${start}, ${end}]`

    await refetchListings(data, { newDateRangeFilters: updatedDateRange }, trackingLabel)
  }

  /**
   * Reset all filters
   */
  const resetAllFilters = async (
    selectedCategoryId?: number,
    newKeyword?: string,
    isDominantCategorySearch?: boolean
  ) => {
    hasUserSelectedSort.current = false
    try {
      await refetchListings(data, {
        additionalFlagFilters: [],
        isDominantCategorySearch,
        newAttributeFilters: [],
        newDateRangeFilters: [],
        newKeyword,
        newPage: 1,
        newRangeFilters: [],
        selectedCategoryId: selectedCategoryId,
        topAdCount: 5,
      })
    } catch (err) {
      /** Track attribute filter search result failed error */
      trackEvent({ action: GA_EVENT.ErrorStateLoad })
      catchError(err)
    }
  }

  /** Fetch all listings as top ads */
  const seeAllTopAds = async (): Promise<void> => {
    try {
      await refetchListings(data, {
        additionalFlagFilters: [AdditionalFlags.TopAd],
        customOffset: 0,
        newPage: 1,
        topAdCount: 0,
      })
    } catch (err) {
      catchError(err)
      refetchListings(data, { newPage: 1 })
    }
  }

  const handleNewSearch = async (selectedCategoryId: number, newKeyword: string) => {
    setLoadingStates({ filters: true })
    try {
      const isDominantCategorySearch = !!newKeyword && selectedCategoryId === 0
      const currentCategory = categoryPath[categoryPath.length - 1]?.id

      //Strip single quotes from user inputted keywords
      //TODO: Investigate why NWA is no longer handling single quotes
      const sanitizedKeyWord = newKeyword?.replace(/'/g, '')

      if (currentCategory === selectedCategoryId) {
        await refetchListings(data, { newKeyword: sanitizedKeyWord })
      } else {
        await resetAllFilters(selectedCategoryId, sanitizedKeyWord, isDominantCategorySearch)
      }
    } catch (error) {
      catchError(error)
    }
  }

  return (
    <SearchContext.Provider
      value={{
        handleDateRangeFiltersChange,
        handleNewSearch,
        handleDateFiltersChange,
        resetDateFilter,
        handleSortByChange,
        loadingFilters,
        loadingResults,
        resetAllFilters,
        seeAllTopAds,
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}
